// src/App.tsx
import { useState, useEffect } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AnimatePresence } from "motion/react";

import Layout from "./components/Layout";
import Home from "./pages/Home";
import CaseStudies from "./pages/CaseStudies";
import CaseStudyDetail from "./pages/CaseStudyDetail";
import About from "./pages/About";
import Contact from "./pages/Contact";
import ErrorPage from "./pages/ErrorPage";
import MaintenanceMode from "./components/MaintenanceMode";
import LoadingBar from "./components/LoadingBar";
import BackToTop from "./components/BackToTop";

// ---- Maintenance: single source of truth ----------------------
const getInitialMaintenance = () => {
  // Highest priority: URL override ?maintenance=1 or ?maintenance=0
  const qs = new URLSearchParams(window.location.search);
  if (qs.get("maintenance") === "1") return true;
  if (qs.get("maintenance") === "0") return false;

  // Persisted choice (from your toggle / exit button)
  const stored = localStorage.getItem("maintenance");
  if (stored !== null) return stored === "true";

  // Build-time default (Cloudflare/locally via .env)
  return import.meta.env.VITE_MAINTENANCE === "true";
};
// ---------------------------------------------------------------

export default function App() {
  const [isLoading, setIsLoading] = useState(false);
  const [isMaintenanceMode, setIsMaintenanceMode] = useState(getInitialMaintenance);
  const location = useLocation();

  // persist maintenance choice
  useEffect(() => {
    localStorage.setItem("maintenance", String(isMaintenanceMode));
  }, [isMaintenanceMode]);

  // fake page loading bar
  useEffect(() => {
    setIsLoading(true);
    const t = setTimeout(() => setIsLoading(false), 400);
    return () => clearTimeout(t);
  }, [location.pathname]);

  // keyboard toggle: Ctrl + Shift + M
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key.toLowerCase() === "m") {
        setIsMaintenanceMode((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // After other useEffects in App()
useEffect(() => {
  // If URL explicitly sets maintenance, respect that and do nothing here
  const qs = new URLSearchParams(window.location.search);
  if (qs.has("maintenance")) return;

  // Fetch the JSON toggle (same idea as your old SPA)
  fetch(`/data/maintenance.json?ts=${Date.now()}`, { cache: "no-store" })
    .then(r => r.ok ? r.json() : { enabled: false })
    .then(cfg => {
      const on = Boolean(cfg?.enabled);
      setIsMaintenanceMode(on);
      localStorage.setItem("maintenance", String(on)); // persist for consistency
    })
    .catch(() => { /* ignore network/cache issues */ });
}, []);


  if (isMaintenanceMode) {
    return <MaintenanceMode onExit={() => setIsMaintenanceMode(true)} />;
  }

  return (
    <div>
      <Layout>
        <LoadingBar active={isLoading} />
        <AnimatePresence mode="wait">
          <Routes location={location} key={location.pathname}>
            <Route path="/" element={<Home />} />
            <Route path="/case-studies" element={<CaseStudies />} />
            <Route path="/case-studies/:slug" element={<CaseStudyDetail />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            {/* Legacy redirects (optional) */}
            <Route path="/work" element={<Navigate to="/case-studies" replace />} />
            <Route path="/research" element={<Navigate to="/case-studies" replace />} />
            {/* Error routes */}
            <Route path="/404" element={<ErrorPage code={404} />} />
            <Route path="/410" element={<ErrorPage code={410} />} />
            <Route path="/451" element={<ErrorPage code={451} />} />
            <Route path="/500" element={<ErrorPage code={500} />} />
            {/* Catch-all */}
            <Route path="*" element={<Navigate to="/404" replace />} />
          </Routes>
        </AnimatePresence>
      </Layout>
      <BackToTop />
    </div>
  );
}
